// src/pages/admin/LocalGigsAdmin.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { logout } from '../../services/authService';
import {
  getPendingJobs, approveJob, deleteJob, updateJob,
  bulkApproveJobs, bulkDeleteJobs,
  adminGrantCredits,
} from '../../services/firestoreService';
import JobCard from '../../components/jobs/JobCard';
import Button from '../../components/ui/Button';
import styles from './AdminDashboard.module.css';

export default function LocalGigsAdmin() {
  const { currentUser, userRole } = useAuth();
  const navigate = useNavigate();

  const [pendingGigs,   setPendingGigs]   = useState([]);
  const [selected,      setSelected]      = useState(new Set());
  const [loading,       setLoading]       = useState(true);
  const [message,       setMessage]       = useState({ text: '', type: 'success' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingId,     setEditingId]     = useState(null);
  const [editDraft,     setEditDraft]     = useState({});

  // Grant credits UI state
  const [grantUid,    setGrantUid]    = useState('');
  const [grantAmount, setGrantAmount] = useState(5);
  const [granting,    setGranting]    = useState(false);

  useEffect(() => {
    if (userRole !== 'admin') { navigate('/dashboard'); return; }
    loadPending();
  }, [userRole, navigate]);

  const flash = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: 'success' }), 4000);
  };

  const loadPending = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const gigs = await getPendingJobs();
      setPendingGigs(gigs);
    } catch {
      flash('Failed to load pending gigs.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Single operations ──
  const handleApprove = async (jobId) => {
    try {
      await approveJob(jobId, true);
      setPendingGigs(prev => prev.filter(g => g.id !== jobId));
      setSelected(prev => { const s = new Set(prev); s.delete(jobId); return s; });
      flash('Gig approved.');
    } catch { flash('Failed to approve gig.', 'error'); }
  };

  const handleDeleteClick = (jobId) => {
    if (confirmDelete === jobId) {
      executeDelete(jobId);
    } else {
      setConfirmDelete(jobId);
      setTimeout(() => setConfirmDelete(c => c === jobId ? null : c), 4000);
    }
  };

  const executeDelete = async (jobId) => {
    setConfirmDelete(null);
    try {
      await deleteJob(jobId);
      setPendingGigs(prev => prev.filter(g => g.id !== jobId));
      setSelected(prev => { const s = new Set(prev); s.delete(jobId); return s; });
      flash('Gig deleted.');
    } catch { flash('Failed to delete gig.', 'error'); }
  };

  // ── Inline edit ──
  const startEdit = (job) => {
    setEditingId(job.id);
    setEditDraft({
      title:       job.title       || '',
      company:     job.company     || '',
      location:    job.location    || '',
      salary:      job.salary      || '',
      description: job.description || '',
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const handleSave = async (jobId, andApprove = false) => {
    try {
      await updateJob(jobId, editDraft);
      if (andApprove) {
        await approveJob(jobId, true);
        setPendingGigs(prev => prev.filter(g => g.id !== jobId));
        flash('Gig saved and approved.');
      } else {
        setPendingGigs(prev => prev.map(g => g.id === jobId ? { ...g, ...editDraft } : g));
        flash('Gig updated.');
      }
      setSelected(prev => { const s = new Set(prev); s.delete(jobId); return s; });
      setEditingId(null);
      setEditDraft({});
    } catch (err) { flash('Save failed: ' + err.message, 'error'); }
  };

  // ── Bulk operations ──
  const toggleSelect = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === pendingGigs.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pendingGigs.map(g => g.id)));
    }
  };

  const handleBulkApprove = async () => {
    if (!selected.size) return;
    try {
      await bulkApproveJobs([...selected]);
      setPendingGigs(prev => prev.filter(g => !selected.has(g.id)));
      flash(`${selected.size} gig${selected.size !== 1 ? 's' : ''} approved.`);
      setSelected(new Set());
    } catch (err) { flash('Bulk approve failed: ' + err.message, 'error'); }
  };

  const handleBulkDelete = async () => {
    if (!selected.size) return;
    try {
      await bulkDeleteJobs([...selected]);
      setPendingGigs(prev => prev.filter(g => !selected.has(g.id)));
      flash(`${selected.size} gig${selected.size !== 1 ? 's' : ''} deleted.`);
      setSelected(new Set());
    } catch (err) { flash('Bulk delete failed: ' + err.message, 'error'); }
  };

  // ── Grant credits ──
  const handleGrantCredits = async (e) => {
    e.preventDefault();
    if (!grantUid.trim() || !grantAmount) return;
    setGranting(true);
    try {
      await adminGrantCredits(grantUid.trim(), Number(grantAmount), currentUser.uid);
      flash(`Granted ${grantAmount} credits to user.`);
      setGrantUid('');
      setGrantAmount(5);
    } catch (err) {
      flash('Grant failed: ' + err.message, 'error');
    } finally {
      setGranting(false);
    }
  };

  if (loading) return <div className={styles.loading}>Loading pending gigs…</div>;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.brand} onClick={() => navigate('/dashboard')}>
          <span className={styles.logo}>GS</span>
          <span className={styles.appName}>Local Gigs Admin</span>
        </div>
        <nav className={styles.nav}>
          <button className={styles.navLink} onClick={() => navigate('/dashboard')}>Dashboard</button>
          <button className={styles.navLink} onClick={() => navigate('/jobs')}>Jobs</button>
          <button className={styles.navLink} onClick={() => navigate('/admin/dashboard')}>Admin Portal</button>
          <button className={`${styles.navLink} ${styles.active}`}>Local Gigs</button>
        </nav>
        <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate('/login'); }}>
          Sign out
        </Button>
      </header>

      <main className={styles.main}>
        {message.text && (
          <div className={`${styles.message} ${message.type === 'error' ? styles.messageError : ''}`}>
            {message.text}
          </div>
        )}

        {/* ── Pending gigs ── */}
        <div className={styles.section}>
          <div className={styles.sectionTopRow}>
            <div>
              <h1>Pending Gigs ({pendingGigs.length})</h1>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 14, marginTop: 4 }}>
                Review and approve freelance gigs scraped from SA marketplaces.
              </p>
            </div>
            <Button onClick={loadPending} variant="secondary" size="sm" disabled={loading}>
              Refresh
            </Button>
          </div>

          {pendingGigs.length > 0 && (
            <div className={styles.bulkBar}>
              <label className={styles.checkAll}>
                <input
                  type="checkbox"
                  checked={selected.size === pendingGigs.length && pendingGigs.length > 0}
                  onChange={toggleSelectAll}
                />
                {selected.size > 0
                  ? `${selected.size} selected`
                  : 'Select all'}
              </label>

              {selected.size > 0 && (
                <div className={styles.bulkActions}>
                  <Button onClick={handleBulkApprove} variant="primary" size="sm">
                    Approve {selected.size}
                  </Button>
                  <Button onClick={handleBulkDelete} variant="destructive" size="sm">
                    Delete {selected.size}
                  </Button>
                </div>
              )}
            </div>
          )}

          {pendingGigs.length === 0 ? (
            <div className={styles.empty}>
              <p>No pending gigs. All caught up, or the scraper hasn't run yet.</p>
              <p style={{ fontSize: 13, marginTop: 8 }}>Run: <code>cd scraper && node run.js</code></p>
            </div>
          ) : (
            <div className={styles.grid} style={{ marginTop: 'var(--space-lg)' }}>
              {pendingGigs.map(job => (
                <div key={job.id} className={`${styles.jobCard} ${selected.has(job.id) ? styles.jobCardSelected : ''}`}>
                  <label className={styles.selectCheck}>
                    <input
                      type="checkbox"
                      checked={selected.has(job.id)}
                      onChange={() => toggleSelect(job.id)}
                    />
                  </label>
                  <JobCard job={editingId === job.id ? { ...job, ...editDraft } : job} adminView />

                  {editingId === job.id ? (
                    <div className={styles.editForm}>
                      <label className={styles.editLabel}>
                        Title
                        <input className={styles.editInput} value={editDraft.title}
                          onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))} />
                      </label>
                      <label className={styles.editLabel}>
                        Company
                        <input className={styles.editInput} value={editDraft.company}
                          onChange={e => setEditDraft(d => ({ ...d, company: e.target.value }))} />
                      </label>
                      <label className={styles.editLabel}>
                        Location
                        <input className={styles.editInput} value={editDraft.location}
                          onChange={e => setEditDraft(d => ({ ...d, location: e.target.value }))} />
                      </label>
                      <label className={styles.editLabel}>
                        Salary
                        <input className={styles.editInput} value={editDraft.salary}
                          onChange={e => setEditDraft(d => ({ ...d, salary: e.target.value }))}
                          placeholder="e.g. R15 000/month" />
                      </label>
                      <label className={styles.editLabel}>
                        Description
                        <textarea className={styles.editTextarea} value={editDraft.description}
                          onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))} />
                      </label>
                      <div className={styles.editActions}>
                        <Button onClick={() => handleSave(job.id, true)} size="sm" variant="primary">
                          Save &amp; Approve
                        </Button>
                        <Button onClick={() => handleSave(job.id, false)} size="sm" variant="secondary">
                          Save
                        </Button>
                        <Button onClick={cancelEdit} size="sm" variant="ghost">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.actions}>
                      <Button onClick={() => handleApprove(job.id)} size="sm" variant="primary">
                        Approve
                      </Button>
                      <Button onClick={() => startEdit(job)} size="sm" variant="secondary">
                        Edit
                      </Button>
                      <Button onClick={() => handleDeleteClick(job.id)} variant="destructive" size="sm">
                        {confirmDelete === job.id ? 'Confirm?' : 'Delete'}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Grant credits ── */}
        <div className={styles.section}>
          <h2 style={{ marginBottom: 8 }}>Grant Credits</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 'var(--space-lg)' }}>
            Manually add credits to a user account by their Firebase UID.
          </p>
          <form onSubmit={handleGrantCredits} className={styles.grantForm}>
            <input
              className={styles.grantInput}
              type="text"
              placeholder="User UID (from Firebase Console)"
              value={grantUid}
              onChange={e => setGrantUid(e.target.value)}
              required
            />
            <input
              className={styles.grantInput}
              type="number"
              min="1"
              max="1000"
              value={grantAmount}
              onChange={e => setGrantAmount(e.target.value)}
              style={{ maxWidth: 100 }}
              required
            />
            <Button type="submit" variant="primary" size="sm" loading={granting}>
              Grant Credits
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
